import { useContext, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import AppContext from '../AppContext';
import WrappedSpinner from 'src/shared/WrappedSpinner';
import { useTranslation } from 'react-i18next';
import { getJoinOrganizationPagePath } from './joinOrganization/JoinOrganizationPage';
import { useOrgWideEventSubscription } from 'src/graphql/useOrgWideEventSubscription';

export default function OrganizationRouteBoundary() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { user } = useContext(AppContext);
    const { orgId } = useParams();

    const userIsInvitedToTargetOrganization = Boolean(
        // If the user tries to acces a direct link to the organization the user is invited to,
        // we redirect the user to the Join page.
        // We can inject the user ID from the token here as we are already sure that the user is logged in.
        // The user ID from the invitation email is only relevant if the user has not signed up yet.
        user && user.orgIdFromInvitation && orgId === user.orgIdFromInvitation,
    );

    useOrgWideEventSubscription({ orgId, enabled: !userIsInvitedToTargetOrganization });

    useEffect(() => {
        if (!orgId || userIsInvitedToTargetOrganization) {
            return;
        }

        // on orgId change (e.g. when user switches orgs), invalidate query (mark as stale)
        // and fetch fresh data
        void queryClient.invalidateQueries({ queryKey: ['getOrganization', orgId], exact: true });
    }, [queryClient, orgId, userIsInvitedToTargetOrganization]);

    useEffect(() => {
        if (userIsInvitedToTargetOrganization) {

            navigate(getJoinOrganizationPagePath(user!.userId, user!.orgIdFromInvitation!, user!.orgNameFromInvitation), { replace: true });
        }
    }, [userIsInvitedToTargetOrganization, user, navigate, orgId]);

    if (userIsInvitedToTargetOrganization) {
        return <WrappedSpinner label={t('Common.Spinner', 'Loading...')} labelPosition="bottom" />;
    }

    return <Outlet />;
}
